import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { PackerEnvelope } from 'src/app/wellbore-inputs/models/packers.model';

export const DEFAULT_PACKER_ENVELOPE_ROW: PackerEnvelope = {
  force: 0,
  differentialPressure: 0
};

export function createPackerEnvelopeColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'force',
      header: 'Force',
      unitKey: 'force',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'differentialPressure',
      header: 'Differential Pressure',
      unitKey: 'pressure',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    }
  ];
}

export function createPackerEnvelopeRow(data?: Partial<PackerEnvelope>): FormGroup {
  return new FormGroup({
    force: new FormControl<number | null>(data?.force ?? null, [Validators.required]),
    differentialPressure: new FormControl<number | null>(data?.differentialPressure ?? null, [Validators.required])
  });
}

export function isPackerEnvelopeRowEmpty(row: FormGroup): boolean {
  const force = row.get('force')?.value;
  const diffPress = row.get('differentialPressure')?.value;

  const isForceEmpty = force === null || force === undefined || force === '' || Number.isNaN(Number(force));
  const isDiffPressEmpty = diffPress === null || diffPress === undefined || diffPress === '' || Number.isNaN(Number(diffPress));

  return isForceEmpty && isDiffPressEmpty;
}

export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createPackerEnvelopeRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isPackerEnvelopeRowEmpty(formArray.at(formArray.length - 1)) &&
    isPackerEnvelopeRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  const lastRow = formArray.at(formArray.length - 1);
  if (!isPackerEnvelopeRowEmpty(lastRow)) {
    formArray.push(createPackerEnvelopeRow());
  }
}

export function populatePackerEnvelopeFormArray(
  formArray: FormArray<FormGroup>,
  envelopeData?: readonly PackerEnvelope[]
): void {
  formArray.clear();
  const rows = envelopeData && envelopeData.length > 0
    ? envelopeData
    : [];

  rows.forEach((item) => {
    formArray.push(createPackerEnvelopeRow(item));
  });

  ensureTrailingInputRow(formArray);
}

export function extractPackerEnvelopeRows(formArray: FormArray<FormGroup>): PackerEnvelope[] {
  const validRows: PackerEnvelope[] = formArray.controls
    .filter((control) => !isPackerEnvelopeRowEmpty(control as FormGroup))
    .map((control) => ({
      force: Number(control.get('force')?.value ?? 0),
      differentialPressure: Number(control.get('differentialPressure')?.value ?? 0)
    }));

  return validRows.length === 0 ? [DEFAULT_PACKER_ENVELOPE_ROW] : validRows;
}
